import { apiSlice } from '../../store/apiSlice';

const submitRatingAndCommentTogether = async (
  { fishery_id, score, comments },
  api,
  extraOptions,
  executeBaseQuery
) => {
  const requests = [];

  requests.push(
    executeBaseQuery({
      url: 'fishery/rating',
      method: 'post',
      data: {
        fishery_id: fishery_id,
        rating: score,
      },
    })
  );

  if (comments) {
    requests.push(
      executeBaseQuery({
        url: 'fishery/comment',
        method: 'post',
        data: {
          fishery_id: fishery_id,
          comment: comments,
        },
      })
    );
  }

  const results = await Promise.all(requests);
  if (results.every((res) => !res?.status || res.status === 200)) {
    return { data: results[0].data.result };
  } else {
    const error = results.find((res) => res?.status && res.data.status !== 200);
    return {
      error: {
        status: error.data.status,
        data: error.data,
      },
    };
  }
};

export const extendedApiSlice = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['Fishery', 'Quick Facts', 'Facilities', 'user_membership'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getFishery: builder.query({
        query: (fisheryPublicId) => ({
          url: `fishery`,
          method: 'get',
          params: { id: fisheryPublicId },
        }),
        providesTags: (result, error) =>
          error ? [] : [{ type: 'Fishery', id: result?.fishery_id }],
      }),
      getMembershipPlans: builder.query({
        query: ({ data }) => ({
          url: `fishery/membership_plans`,
          method: 'get',
          params: { fishery_id: data },
          useDefaultErrorHandler: false,
        }),
        providesTags: (result, error) =>
          error ? [] : [{ type: 'Fishery_membership', id: result?.fishery_id }],
      }),
      getUserMemberships: builder.query({
        query: ({ data }) => ({
          url: `user/memberships`,
          method: 'get',
        }),
        providesTags: (result, error) =>
          error ? [] : [{ type: 'user_membership' }],
      }),
      getMembershipsPaymentLink: builder.query({
        query: ({ plan_id }) => ({
          url: `user/memberships/payment_link`,
          method: 'get',
          params: { plan_id: plan_id },
        }),
        providesTags: (result, error) =>
          error ? [] : [{ type: 'user_membership' }],
      }),

      requestMembership: builder.mutation({
        query: (data) => ({
          url: 'user/memberships/request',
          method: 'post',
          data: data,
        }),
        invalidatesTags: (result, error, { fishery_id }) => [
          { type: 'user_membership' },
        ],
      }),
      markFisheryAsFavourite: builder.mutation({
        query: (fishery_id) => ({
          url: 'fav',
          method: 'post',
          data: {
            fishery_id: fishery_id,
          },
        }),
        invalidatesTags: (result, error, { fishery_id }) => [
          { type: 'Fishery', id: fishery_id },
          { type: 'Favourite' },
        ],
      }),

      submitReview: builder.mutation({
        queryFn: submitRatingAndCommentTogether,
        invalidatesTags: (result, error, { fishery_id }) => [
          { type: 'Fishery', id: fishery_id },
        ],
      }),

      getAllQuickFacts: builder.query({
        query: () => ({
          url: `fishery/quick_facts`,
          method: 'get',
        }),
        providesTags: (result, error) => [
          ...(error
            ? []
            : result.map(({ id }) => ({ type: 'Quick Facts', id }))),
          { type: 'Quick Facts', id: 'ALL' },
        ],
      }),

      getAllFacilities: builder.query({
        query: () => ({
          url: `fishery/facilities`,
          method: 'get',
        }),
        providesTags: (result, error) => [
          ...(error
            ? []
            : result.map(({ id }) => ({ type: 'Facilities', id }))),
          { type: 'Facilities', id: 'ALL' },
        ],
      }),
    }),
  });

export const {
  useGetFisheryQuery,
  useGetMembershipPlansQuery,
  useGetUserMembershipsQuery,
  useMarkFisheryAsFavouriteMutation,
  useSubmitReviewMutation,
  useGetAllQuickFactsQuery,
  useGetAllFacilitiesQuery,
  useRequestMembershipMutation,
  useGetMembershipsPaymentLinkQuery,
} = extendedApiSlice;
